import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import MobileBottomNav from "../../components/feature/MobileBottomNav";
import { verifySellPaymentApi } from "../../api/apiServices";
import html2canvas from "html2canvas";

export default function SellCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const cardRef = useRef(null);

  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState("");
  const [transaction, setTransaction] = useState(null);

  const reference = searchParams.get("reference") || searchParams.get("ref");

  useEffect(() => {
    if (!reference) {
      setStatus("error");
      setMessage("No transaction reference found");
      return;
    }

    const verifyPayment = async () => {
      try {
        const data = await verifySellPaymentApi(reference);
        if (data.status === true) {
          const tx = data.data;
          setTransaction(tx);

          if (tx.status === "completed") {
            setStatus("success");
            setMessage("Your Naira has been sent successfully!");
          } else if (tx.status === "failed") {
            setStatus("failed");
            setMessage("Transfer failed. Your crypto has been refunded.");
          } else {
            setStatus("processing");
            setMessage("Your transfer is being processed...");
          }
        } else {
          setStatus("error");
          setMessage(data.message || "Verification failed");
        }
      } catch (err) {
        console.error(err);
        setStatus("error");
        setMessage("Unable to verify transfer. Please contact support.");
      }
    };

    verifyPayment();
  }, [reference]);

  // Download as Image
  const downloadAsImage = async () => {
    if (!cardRef.current) return;

    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        logging: false,
      });

      const link = document.createElement("a");
      link.download = `Nivora_Sell_${reference}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      alert("Failed to download image");
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-28 flex flex-col items-center justify-center px-5">
      {status === "verifying" && (
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-6"></div>
          <h2 className="text-xl font-bold">Verifying Transfer</h2>
          <p className="text-slate-500 mt-2">Please wait...</p>
        </div>
      )}

      {status === "success" && transaction && (
        <>
          <div className="w-24 h-24 rounded-full bg-emerald-100 border-4 border-emerald-200 flex items-center justify-center mb-6">
            <i className="ri-check-line text-emerald-600 text-7xl"></i>
          </div>

          <h2 className="text-3xl font-bold text-emerald-600 mb-1">Transfer Successful!</h2>
          <p className="text-slate-600 mb-8 text-center max-w-xs">{message}</p>

          {/* Rich Shareable Receipt Card */}
          <div 
            ref={cardRef}
            className="bg-white rounded-3xl shadow-xl p-8 w-full max-w-md mb-8 overflow-hidden relative"
          >
            <div className="absolute top-6 right-6 text-emerald-600">
              <i className="ri-verified-badge-fill text-3xl"></i>
            </div>

            <div className="text-center mb-8">
              <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mb-3">
                <i className="ri-arrow-up-line text-4xl text-emerald-600"></i>
              </div>
              <p className="text-emerald-600 font-semibold">SOLD SUCCESSFULLY</p>
              <p className="text-4xl font-bold text-slate-800 mt-2">
                ₦{parseFloat(transaction.nairaAmount).toLocaleString()}
              </p>
            </div>

            <div className="space-y-5 border-t border-b border-dashed py-6">
              <div className="flex justify-between">
                <span className="text-slate-500">Asset Sold</span>
                <span className="font-semibold">{transaction.cryptoAmount} {transaction.asset}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Rate</span>
                <span>₦{transaction.rate?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Fee</span>
                <span className="text-red-600">-₦{transaction.fee?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between pt-4 border-t">
                <span className="font-semibold">You Received</span>
                <span className="font-bold text-emerald-600">₦{parseFloat(transaction.nairaAmount).toLocaleString()}</span>
              </div>
            </div>

            <div className="pt-6 space-y-4 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">To</span>
                <span className="text-right">{transaction.accountName}<br/>{transaction.bankName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Account</span>
                <span className="font-mono">{transaction.accountNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Reference</span>
                <span className="font-mono text-xs">{reference}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Date</span>
                <span>{new Date(transaction.createdAt).toLocaleString()}</span>
              </div>
            </div>

            <div className="text-center mt-8 text-xs text-slate-400">
              Powered by Nivora • Secure Transaction
            </div>
          </div>

          <div className="flex flex-col gap-3 w-full max-w-xs">
            <button 
              onClick={downloadAsImage}
              className="w-full py-4 bg-gradient-to-r from-emerald-600 to-emerald-600 text-white rounded-2xl font-semibold flex items-center justify-center gap-2 shadow-lg"
            >
              <i className="ri-image-line"></i> Download as Image
            </button>

            <button 
              onClick={() => navigate("/home")} 
              className="w-full py-4 bg-white border border-slate-300 text-slate-700 rounded-2xl font-semibold"
            >
              Back to Home
            </button>
          </div>
        </>
      )}

      {/* Failed / Processing States */}
      {(status === "failed" || status === "error" || status === "processing") && (
        <div className="text-center">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${status === "failed" ? "bg-red-100" : "bg-amber-100"}`}>
            <i className={`ri-${status === "failed" ? "close" : "time"}-line text-6xl ${status === "failed" ? "text-red-600" : "text-amber-600"}`}></i>
          </div>
          <h2 className={`text-2xl font-bold mb-2 ${status === "failed" ? "text-red-600" : "text-amber-600"}`}>
            {status === "failed" ? "Transfer Failed" : "Processing Transfer"}
          </h2>
          <p className="text-slate-600 mb-8 max-w-xs">{message}</p>
          <button onClick={() => navigate("/home")} className="w-full max-w-xs py-4 bg-slate-800 text-white rounded-2xl font-semibold">
            Back to Home
          </button>
        </div>
      )}

      <MobileBottomNav />
    </div>
  );
}